/* TBD - partial conversion
 * committed originally in 856a9c65c611ea29789fede18c3119e269f9f6d8 by erodrigues on Aug 4 2023
 * properties should be integrated into the generic context and the functionality reorganized
 */
import { ColorQuality } from "app/modules/remote-control-ng/definitions";
import { BehaviorSubject, Observable } from "rxjs";
import { ElementRect, IBaseDeviceContext, IRcDevice, IDeviceCanvas, IDeviceContext, IDeviceContextSkinInfo, IDeviceViewHandler, IRemoteControlConnection, ResponseData, SOTIHTMLCanvasElement, IDeviceClient } from "../mcrc-api";
import { DeviceCanvas } from "./deviceCanvas";
import { hd } from "@nx-fe/services";
import { DeviceInfo } from "app/modules/remote-control/services/remote-control.connection";
import { RcContext } from "./rcContext";
import { RemoteControlDeviceState } from "app/modules/remote-control/services/device-state-registry.service";

export interface IDeviceContextScreen extends ElementRect {
    dw: number;
    dh: number;
    rotation: number;
}

export interface IRcCanvasElement extends HTMLCanvasElement {
    emptySkin: boolean;
}

export interface IRcDeviceContext extends IDeviceContext {
    agentRecordingAudioBitRate: number;
    agentRecordingAudioSamplingRate: number;
    agentRecordingScreenHeight: number;
    agentRecordingScreenWidth: number;
    agentRecordingVideoBitRate: number;
    agentRecordingVideoFrameRate: number;
    annotationCursorColor: string;
    annotationCursorSize: number;
    annotationTool: number;
    annotationsToolHint: boolean;
    bitDepth: number;
    clientScaleFactor: number;
    clientSideRotation: number;
    canvasBuffer: HTMLCanvasElement;
    cursorX: number;
    cursorY: number;
    deviceHeight: number;
    deviceWidth: number;
    // deviceSupportsScreenProjection: boolean;
    disableMouseInput: boolean;
    // imageQuality: number;
    // rotation: number;
    // scale: number;
    saveScale: number;
    targetClientSideRotation: number;
    targetRotation: number;
    // setRotation(rotation: number): void;
    imageSkin: IRcCanvasElement;
    // transfer(oldRc: IRcDeviceContext): void;
}

export interface IRemoteControlStreamingContext extends IBaseDeviceContext {
    device: hd.IDeviceModel;     // returned from IDeviceService.getDevicesResource()
    // remoteControlSharedData: hd.IRemoteControlModel;    // returned from IDeviceService.getRemoteControlData()
    // remoteControlConnection: IRemoteControlConnection;  // connection to device (WebSocket to DS in case of video streaming, or to RC Service otherwise)
    // TODO 2025.1-XS-23332: check whether can be merged with TransformationHandler
    deviceViewHandler: IDeviceViewHandler;  // handle device screen scaling

    // videoStreaming: boolean;  // true if this context is using video streaming
    permissions: number;

    // compatibility - these variables are defined the same as in mcrc.com.DeviceContext in order to work with legacy JS (e.g. mcrc-comm-DeviceInput.js) without too many changes
    // osType: number;
    // scale: number;            // the actual scale, the ratio of device view size to device physical screen size
    // rotation: number;         // the current device screen orientation
    // screenWidth: number;      // device screen width when rotation == 0
    // screenHeight: number;     // device screen height when rotation == 0
    // disableMouseInput: boolean;
    // deviceSupportsScreenProjection: boolean;
    // imageQuality: number;

    state: RemoteControlDeviceState;
}

export class DeviceContext implements IRcDeviceContext {
    // IBaseDeviceContext
    public remoteControlSharedData: hd.IRemoteControlModel = null;
    public remoteControlConnection: IRemoteControlConnection = null;
    public videoStreaming: boolean = false;

    public deviceCanvas: SOTIHTMLCanvasElement;
    public canvas: IDeviceCanvas;
    public screenWidth: number = 0;
    public screenHeight: number = 0;
    public screenOffsetX: number = 0;
    public screenOffsetY: number = 0;
    public rotation: number = 0;
    public clientSideRotation: number = 0;
    public targetClientSideRotation: number = 0;
    public targetRotation: number = 0;
    public scale: number = 1;
    public saveScale: number = 1;
    public targetScale: number = 0;
    public clientScaleFactor: number = 0;
    public scaleFactor: number = 1.0; // used for transform the canvasOuter
    public deviceSupportsScreenProjection: boolean = false;
    public skinScreenWidth: number = -1;
    public skinScreenHeight: number = -1;
    public deviceWidth: number = -1;
    public deviceHeight: number = -1;
    public osType: number = 0;
    public left: number = 0;
    public top: number = 0;
    public targetX: number = 0;
    public targetY: number = 0;
    public skinDone: boolean;
    public imageQuality: number;
    public bitDepth: ColorQuality;
    private _rotation$: BehaviorSubject<number | undefined>;
    public rotation$: Observable<number | undefined>;
    public annotationCursorSize: number;
    public annotationCursorColor: string;
    public annotationTool: number;
    public annotationsToolHint: boolean;
    public cursorX: number;
    public cursorY: number;
    public canvasBuffer = <HTMLCanvasElement>document.createElement("canvas");
    public debugCanvas = <HTMLCanvasElement>document.createElement("canvas");
    public canvasObject: DeviceCanvas;
    // public canvas3d: any;
    public skinCanvas: HTMLCanvasElement; //not sure about this
    /**
     * imageSkin {
     *  emptySkin: bool
     *  height: number
     *  width: number
     * }
     */
    public imageSkin: IRcCanvasElement;
    public imageCapture: any;  // createImageFromBinaryData used in TransformationHandler will assign HTMLImageElement to this

    // used for translate in Pan mode
    public translateX: number;
    public translateY: number;

    public screen: IDeviceContextScreen;

    public agentRecordingAudioBitRate: number;
    public agentRecordingAudioSamplingRate: number;
    public agentRecordingScreenHeight: number;
    public agentRecordingScreenWidth: number;
    public agentRecordingVideoBitRate: number;
    public agentRecordingVideoFrameRate: number;
    public disableMouseInput: boolean;
    public device: IRcDevice;
    public skin: IDeviceContextSkinInfo;
    public client: IDeviceClient;

    constructor(deviceCanvas: DeviceCanvas) {
        this.canvas = {
            loaded: false,
            width: 0,
            height: 0,
            element: <SOTIHTMLCanvasElement>document.getElementById("rc_canvas"),
            init: undefined,
            clientHeight: undefined,
            clientWidth: undefined
        };

        if (!this.canvas.element) {
            console.log('canvas not found..  video stream ');
            return;
        }

        this.canvas.width = 0;
        this.canvas.height = 0;
        this.canvasObject = deviceCanvas;
        // this.canvas3d = null;
        this.skinCanvas = null;
        this.imageSkin = null;
        this.imageCapture = null;
        this.canvasBuffer.width = 0;
        this.canvasBuffer.height = 0;
        this.canvas.element.style.display = "none";
        this.skinDone = false;
        this.imageQuality = RcContext.instance.imageQuality;
        this.bitDepth = RcContext.instance.bitDepth;
        this.canvas.element.screenWidth = 0;
        this.canvas.element.screenHeight = 0;

        this._rotation$ = new BehaviorSubject(undefined);
        this.rotation$ = this._rotation$.asObservable();

        this.screen = <IDeviceContextScreen>{
            dw: 0,
            dh: 0,
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
        };
    }

    public setRotation = (rotation: number) => {
        this.targetRotation = rotation;
        this._rotation$.next(rotation);
    }

    public transfer = (oldContext: IRcDeviceContext) => {
        // Transfer necessary state from old context
        this.deviceWidth = oldContext.deviceWidth;
        this.deviceHeight = oldContext.deviceHeight;
        this.clientSideRotation = oldContext.clientSideRotation;
        this.targetClientSideRotation = oldContext.targetClientSideRotation;
        this.rotation = oldContext.rotation;
        this.targetRotation = oldContext.targetRotation;
        this.scale = oldContext.scale;
        this.saveScale = oldContext.saveScale;
        this.clientScaleFactor = oldContext.clientScaleFactor;
        this.imageQuality = oldContext.imageQuality;
        this.bitDepth = oldContext.bitDepth;
        this.canvas.element.width = oldContext.canvas.element.width;
        this.canvas.element.height = oldContext.canvas.element.height;
        this.canvas.element.screenWidth = oldContext.canvas.element.screenWidth;
        this.canvas.element.screenHeight = oldContext.canvas.element.screenHeight;

        // Annotations
        this.annotationCursorSize = oldContext.annotationCursorSize;
        this.annotationCursorColor = oldContext.annotationCursorColor;
        this.annotationTool = oldContext.annotationTool;
        this.annotationsToolHint = oldContext.annotationsToolHint;
        this.cursorX = oldContext.cursorX;
        this.cursorY = oldContext.cursorY;

        this.deviceSupportsScreenProjection = oldContext.deviceSupportsScreenProjection;

        // Initialize the size of the new canvas buffer to the old size
        this.canvasBuffer.width = oldContext.canvasBuffer.width;
        this.canvasBuffer.height = oldContext.canvasBuffer.height;
        this.canvas.element.screenWidth = oldContext.canvas.element.screenWidth;
        this.canvas.element.screenHeight = oldContext.canvas.element.screenHeight;
    };

    public clearSkin = () => {
        this.skinCanvas = null;
        this.imageSkin = null;
        this.skinScreenWidth = -1;
        this.skinScreenHeight = -1;
        this.skinDone = false;
    }
}

export class RemoteControlStreamingContext implements IRemoteControlStreamingContext {
    // IBaseDeviceContext
    public remoteControlSharedData: hd.IRemoteControlModel = null;
    public remoteControlConnection: IRemoteControlConnection = null;
    public videoStreaming: boolean = false;

    public device: hd.IDeviceModel;
    public deviceViewHandler: IDeviceViewHandler;
    public permissions: number;
    public osType: number;
    public maxScale: number;
    public scale: number;
    public rotation: number;
    public screenWidth: number;
    public screenHeight: number;
    public disableMouseInput: boolean;
    public deviceSupportsScreenProjection: boolean;
    public imageQuality: number;

    // Moved from NxtRemoteControlScreenService
    // FullScreen message related
    public state: RemoteControlDeviceState;
    public videoPlayerReady: boolean = false;
    public mediaSource: MediaSource = null;
    public video: HTMLVideoElement = null;
    public networkInfo = {
        bytes: 0,
        frames: 0,
        lastAckTime: Date.now(),
        recentKeyframes: [],
        //pingResponseTime: 0,
    };

    // for remote view, it's better to start with non-zero scale, so that PC_RESTART won't be sent until the 1st screen is received, otherwise PC_RESTART might dismiss the remote view popup
    // private $scale = 50;          // since device screen height or rotation is unknown, set to 50% first and will be adjusted later in onWebInitMessage()
    public $scale = 0; // start as 0 so that agent will wait until we send a PC_RESTART message
    public $scaleFactor = 1; // $scale * $scaleFactor = the actual scale sent to device, make it less than 1 when network is slow to reduce network traffic
    public quality = 80; // default to 80, will be adjusted according to network bandwidth
    public lastChangeTimestamp = 0; // the time when last PC_RESTART message is sent, -1 means don't send PC_RESTART until the 1st screen is received

    // Replaced previous deviceInfo in NgRemoteControlConnection
    public deviceInfo: DeviceInfo;

    constructor(deviceModel: hd.IDeviceModel) {
        this.device = deviceModel;
        this.deviceViewHandler = null;
        this.osType = 0;
        this.scale = 0;
        this.rotation = 0;
        this.screenWidth = 0;
        this.screenHeight = 0;
        this.permissions = 0;
        this.disableMouseInput = false;
        this.deviceSupportsScreenProjection = true;
        this.imageQuality = 80;
    }
    skin: IDeviceContextSkinInfo;
}
